/**
 * GameState - Manages the overall game state machine.
 * States: Menu → Playing → HalfTime → ExtraTime → GameOver
 * Emits callbacks on state transitions so other systems can react.
 * Covers tasks 1.14 (halftime), 1.15 (extra time), 8.7 (frame rate limit).
 */

import { DifficultyLevel } from "./DifficultyManager";

export enum State {
  Menu = "Menu",
  Playing = "Playing",
  Paused = "Paused",
  HalfTime = "HalfTime",
  ExtraTime = "ExtraTime",
  GameOver = "GameOver",
}

export interface GameScore {
  player: number;
  opponent: number;
}

// Half-time scores for stats overlay
export interface HalfTimeStats {
  firstHalfScore: GameScore;
}

type StateChangeCallback = (prev: State, next: State) => void;

export class GameState {
  private _state: State = State.Menu;
  private _score: GameScore = { player: 0, opponent: 0 };
  private _matchDurationMinutes = 3; // configurable
  private _matchDuration = 180; // full match in seconds
  private _halfDuration = 90; // half match in seconds
  private _timeRemaining = 180;
  private _showCrowd = true;
  private _difficulty: DifficultyLevel = DifficultyLevel.EASY;
  private _listeners: StateChangeCallback[] = [];

  // Halftime / extra time
  private _isFirstHalf = true;
  private _halfTimeBreakTimer = 0;
  private _halfTimeBreakDuration = 5; // 5 seconds halftime break
  private _firstHalfScore: GameScore = { player: 0, opponent: 0 };
  private _extraTimeEnabled = true;
  private _extraTimeDuration = 60; // 1 minute extra time
  private _inExtraTime = false;

  // Frame rate limiting (task 8.7)
  private _frameRateLimit = 0; // 0 = unlimited
  private _lastFrameTime = 0;

  get state(): State {
    return this._state;
  }

  get difficulty(): DifficultyLevel {
    return this._difficulty;
  }

  setDifficulty(level: DifficultyLevel): void {
    this._difficulty = level;
  }

  get showCrowd(): boolean {
    return this._showCrowd;
  }

  set showCrowd(value: boolean) {
    this._showCrowd = value;
  }

  get score(): GameScore {
    return { ...this._score };
  }

  get timeRemaining(): number {
    return this._timeRemaining;
  }

  get matchDuration(): number {
    return this._matchDuration;
  }

  get isFirstHalf(): boolean {
    return this._isFirstHalf;
  }

  get inExtraTime(): boolean {
    return this._inExtraTime;
  }

  get firstHalfScore(): GameScore {
    return { ...this._firstHalfScore };
  }

  /** Set match duration in minutes */
  setMatchDuration(minutes: number): void {
    this._matchDurationMinutes = minutes;
    this._matchDuration = minutes * 60;
    this._halfDuration = this._matchDuration / 2;
    this._timeRemaining = this._matchDuration;
  }

  /** Set frame rate limit (0 = unlimited) */
  setFrameRateLimit(fps: number): void {
    this._frameRateLimit = fps;
  }

  getFrameRateLimit(): number {
    return this._frameRateLimit;
  }

  /** Register a callback for state changes */
  onChange(cb: StateChangeCallback): void {
    this._listeners.push(cb);
  }

  /** Transition to a new state */
  setState(next: State): void {
    if (next === this._state) return;
    const prev = this._state;
    this._state = next;
    this._listeners.forEach((cb) => cb(prev, next));
  }

  /** Add a goal for the given side */
  addGoal(side: "player" | "opponent"): void {
    this._score[side]++;
  }

  /**
   * Tick the timer. 
   * Returns: "halftime" if first half ended, "extratime" if needs extra time, "gameover" if time up, null otherwise.
   */
  tick(dt: number): "halftime" | "extratime" | "gameover" | null {
    if (this._state !== State.Playing) return null;

    this._timeRemaining -= dt;

    // First half ends at half duration
    if (this._isFirstHalf && this._timeRemaining <= this._halfDuration) {
      this._isFirstHalf = false;
      this._firstHalfScore = { ...this._score };
      return "halftime";
    }

    if (this._timeRemaining <= 0) {
      this._timeRemaining = 0;

      // Check for extra time (tied match)
      if (this._extraTimeEnabled && !this._inExtraTime && this._score.player === this._score.opponent) {
        this._inExtraTime = true;
        this._timeRemaining = this._extraTimeDuration;
        return "extratime";
      }

      return "gameover";
    }

    return null;
  }

  /** Get half-time break timer */
  tickHalfTimeBreak(dt: number): boolean {
    this._halfTimeBreakTimer += dt;
    return this._halfTimeBreakTimer >= this._halfTimeBreakDuration;
  }

  resetHalfTimeBreak(): void {
    this._halfTimeBreakTimer = 0;
  }

  /** Reset everything for a new match */
  reset(): void {
    this._score = { player: 0, opponent: 0 };
    this._timeRemaining = this._matchDuration;
    this._isFirstHalf = true;
    this._inExtraTime = false;
    this._halfTimeBreakTimer = 0;
    this._firstHalfScore = { player: 0, opponent: 0 };
  }
}
